// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ActionButton, CashInMethod, ErrorCause, Feedback } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogUpdateCashInMethodStatusProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogUpdateCashInMethodStatus = ({
	isOpen,
	onClose,
	onSuccess,
}: DialogUpdateCashInMethodStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogCashInMethod = searchParams.get('dialogCashInMethod');
	const paramDialogStatus = searchParams.get('dialogStatus');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [cashInMethod, setCashInMethod] = useState<CashInMethod | null>(null);

	// fetch cash-in method
	const { isLoading: isLoadingCashInMethod } = useFetch({
		isEnabled: Boolean(paramDialogCashInMethod && isOpen),
		url: `${process.env.REACT_APP_API_URL}/cash-in-methods/${paramDialogCashInMethod}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCashInMethod(res.data[0]);
			}
		},
	});

	// vars
	const isActivating = paramDialogStatus === 'ACTIVE';

	// dialog configuration
	const dialog = isActivating
		? {
				alert: t('alert.activateCashInMethod'),
				btnSubmit: {
					children: t('activateCashInMethod'),
					color: 'primary' as const,
				},
				i18nKey: 'dialogUpdateCashInMethodStatus.activate.content',
				title: t('activateCashInMethod'),
		  }
		: {
				alert: t('alert.deactivateCashInMethod'),
				btnSubmit: {
					children: t('deactivateCashInMethod'),
					color: 'error' as const,
				},
				i18nKey: 'dialogUpdateCashInMethodStatus.deactivate.content',
				title: t('deactivateCashInMethod'),
		  };

	const handleSubmit = async () => {
		setFeedback(null);

		try {
			const fetchUpdateCashInMethodStatus = await fetch(
				`${process.env.REACT_APP_API_URL}/cash-in-methods/${paramDialogCashInMethod}`,
				{
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify({
						status: paramDialogStatus,
					}),
				}
			);

			const resUpdateCashInMethodStatus = await fetchUpdateCashInMethodStatus.json();

			if (resUpdateCashInMethodStatus.error) {
				throw new Error(resUpdateCashInMethodStatus.error.message, {
					cause: resUpdateCashInMethodStatus.error,
				});
			}

			// success banner
			setBanner({
				hasClose: true,
				message: t(isActivating ? 'alert.cashInMethodActivated' : 'alert.cashInMethodDeactivated', {
					name: cashInMethod?.name,
				}),
				severity: 'success',
			});

			// close dialog
			onClose();

			// callback
			if (onSuccess) {
				onSuccess();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause?.id) {
				if (cause.id === 'CASH_IN_METHOD_UNAUTHORIZED') {
					message = t('error.unauthorized');
				}
			}

			setFeedback({
				alert: {
					children: message,
					severity: 'error',
				},
			});
		}
	};

	// render content
	const renderContent = () => {
		// loading
		if (isLoadingCashInMethod) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		// error
		if (feedback?.alert) {
			return (
				<Grid item xs={12}>
					<Alert severity={feedback.alert.severity}>
						<AlertTitle>{t('error')}</AlertTitle>
						{feedback.alert.children}
					</Alert>
				</Grid>
			);
		}

		// success
		return (
			<>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.i18nKey}
						values={{
							name: cashInMethod?.name,
						}}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{t(dialog.alert)}</Alert>
				</Grid>
			</>
		);
	};

	// action buttons
	const actionButtons: ActionButton[] = [
		{
			children: t('cancel'),
			onClick: onClose,
			variant: 'text',
		},
		{
			...dialog.btnSubmit,
			loading: false,
			onClick: handleSubmit,
			variant: 'contained',
		},
	];

	// dialog props
	const dialogProps: Partial<DialogActionsProps> = {};
	if (cashInMethod) {
		dialogProps.subtitle = {
			children: cashInMethod.name,
		};
	}

	return (
		<DialogActions
			actions={actionButtons}
			alignItems="center"
			hasClose={true}
			IconCircleProps={{
				type: 'cashInMethod',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={onClose}
			title={{ children: dialog.title }}
			{...dialogProps}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
